/**
 *  TecnomatGoogleChannelSidebar script file
**/

import authService, { USER_ROLES } from '@/api/authService'
import googleAdsService, {
  MAX_ROAS_GOAL,
  MIN_ROAS_GOAL,
  PRIORITY_OPTIONS,
  STRATEGY_OPTIONS_LIA,
  STRATEGY_OPTIONS_SEARCH,
  STRATEGY_VALUES,
  GOOGLE_SEARCH_NETWORKS
} from '@/api/googleAdsService'
import customFormatters from '@/filters/customFormatters'
import i18n from '@/i18n'
import { CAMPAIGN_STATUSES, CHANNEL_OPTIONS, CHANNEL_PRODUCT_TYPES, CHANNEL_DV360_OPTIONS } from '@/utils/campaignOptions'
import moment from 'moment'
import { isEqual } from 'lodash'
import dv360Service, { DV360_CAMPAIGN_GOAL_OPTIONS, DV360_FRECUENCY_CAP_PERIOD_OPTIONS, DV360_PACING_PERIOD_OPTIONS, DV360_PACING_TYPE_OPTIONS, DV360_PERFORMANCE_GOAL_OPTIONS } from '@/api/dv360Service'

const allOption = () => ({ id: 'all', label: i18n.t('ALL_ZIPCODES'), name: i18n.t('ALL_ZIPCODES'), code: i18n.t('ALL_ZIPCODES') })
// const DEBOUNCE_TIME = 500

// how many insertion order items can be added to the list
const INSERTION_ORDER_LIMIT = 1

// how many line item items can be added to the list
const LINE_ITEM_LIMIT = 1

export default {
  name: 'TecnomatGoogleChannelSidebar',
  mixins: [customFormatters],
  components: {
    TagListSelector: () => import('@/components/tag-list-selector/TagListSelector.vue'),
    KeywordSelector: () => import('@/components/keyword-selector/KeywordSelector.vue')
  },
  props: {
    campaign: {
      type: Object,
      required: true
    },
    channel: {
      type: Object,
      required: true
    },
    zones: {
      type: Array,
      required: true
    },
    zonesMeta: {
      type: Object,
      default: () => ({ loading: false, count: 0 })
    },
    boxSelectionEnabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      authData: authService.authData,
      areasHelp: {
        title: i18n.t('AREAS_HELP_TITLE'),
        text: i18n.t('AREAS_HELP_BODY')
      },
      formatsHelp: {
        title: i18n.t('FORMAT_HELP_TITLE'),
        text: i18n.t('FORMAT_HELP_TEXT')
      },
      merchantOptions: [],
      priorityOptions: PRIORITY_OPTIONS,
      networkOptions: GOOGLE_SEARCH_NETWORKS,
      campaignGoalOptions: DV360_CAMPAIGN_GOAL_OPTIONS,
      performanceGoalOptions: DV360_PERFORMANCE_GOAL_OPTIONS,
      frecuencyCapPeriodOptions: DV360_FRECUENCY_CAP_PERIOD_OPTIONS,
      pacingPeriodOptions: DV360_PACING_PERIOD_OPTIONS,
      pacingTypeOptions: DV360_PACING_TYPE_OPTIONS,
      creativeOptions: [], // fetched from endpoint
      creativeOptionsFiltered: [],
      targetTypeOptions: [], // fetched from endpoint
      newHeadline: '',
      newDescription: '',
      errors: [],
      initialChannel: null,
      newInsertionOrder: {
        name: '',
        pacingPeriod: null,
        pacingType: null,
        pacingMaxDailySpend: 0
      },
      newLineItem: {
        name: '',
        markupPercentage: 0,
        creatives: [],
        targetType: null
      },
      channelDV360Options: Object.values(CHANNEL_DV360_OPTIONS),
      campaignOptions: [],
      campaignItemsOptions: [],
      insertionOrderOptions: [],
      insertionOrderItemsOptions: [],
      loadingCampignOptions: false,
      errorFetchingCampaignOptions: '',
      loadingInsertionOrderOptions: false,
      errorFetchingInsertionOrderOptions: ''

    }
  },
  computed: {
    isLIA () {
      return this.type === CHANNEL_OPTIONS.LOCALINVENTORYADS
    },
    isGoogleSearch () {
      return this.type === CHANNEL_OPTIONS.GOOGLESEARCHADS
    },
    isDV360 () {
      return this.type === CHANNEL_OPTIONS.DV360
    },
    showDV360Form () {
      const createMode = (this.isDV360 && this.channel.mode === CHANNEL_DV360_OPTIONS.CREATE_UPDATE.value)
      const isLineSelected = (this.isDV360 && this.channel.mode === CHANNEL_DV360_OPTIONS.ADD_LINE_ITEM.value && this.selectedLineOrderItem)
      return !this.isDV360 || createMode || isLineSelected
    },
    isNewChannel () {
      return !this.channel.id
    },
    isNewLineItemMode () {
      return this.channel.mode === CHANNEL_DV360_OPTIONS.ADD_LINE_ITEM.value
    },
    isMaximizeConversions () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      return value && value === STRATEGY_VALUES.MAX_CONVERSIONS
    },
    isMaximizeClicks () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      return value && value === STRATEGY_VALUES.MAX_CLICKS
    },
    isEnhancedCPC () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      return value && value === STRATEGY_VALUES.ENHANCED_CPC
    },
    isManualCPC () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      return value && value === STRATEGY_VALUES.MANUAL_CPC
    },
    isROASGoal () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      const choices = [STRATEGY_VALUES.ROAS_GOAL, STRATEGY_VALUES.MAX_ROAS]
      return value && choices.indexOf(value) !== -1
    },
    strategyOptions () {
      return this.isLIA ? STRATEGY_OPTIONS_LIA : STRATEGY_OPTIONS_SEARCH
    },
    campaignType () {
      return CHANNEL_PRODUCT_TYPES[this.channel.direction].label
    },
    type () {
      return this.$route.params.channeltype
    },
    selectedNetworks () {
      return this.channel.networks || []
    },
    selectedStrategy () {
      return this.channel.strategy || null
    },
    selectedMerchant () {
      return this.channel.merchant || null
    },
    selectedPriority () {
      return this.channel.priority || null
    },
    selectedCampaignGoal () {
      return typeof this.channel.campaign_goal === 'string' ? this.campaignGoalOptions.find(o => o.value === this.channel.campaign_goal) : this.channel.campaign_goal
    },
    selectedPerformanceGoal () {
      return typeof this.channel.performance_goal === 'string' ? this.performanceGoalOptions.find(o => o.value === this.channel.performance_goal) : this.channel.performance_goal
    },
    selectedFrecuencyCapPeriod () {
      return typeof this.channel.frecuency_cap_period === 'string' ? this.frecuencyCapPeriodOptions.find(o => o.value === this.channel.frecuency_cap_period) : this.channel.frecuency_cap_period
    },
    selectedCreatives () {
      return this.newLineItem.creatives || []
    },
    selectedCampaign () {
      return this.channel.campaign || null
    },
    selectedLineOrderItem () {
      return this.channel.line_item || null
    },
    startDate () {
      return this.channel.start_date || this.campaign.start_date
    },
    finishDate () {
      return this.channel.finish_date || this.campaign.finish_date
    },
    selectableDates () {
      return [
        this.campaign.start_date,
        this.campaign.finish_date
      ]
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    readonly () {
      const { CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE } = CAMPAIGN_STATUSES
      return this.channel.id && [CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE].indexOf(this.campaign.status) !== -1
    },
    readonlyAreas () {
      return this.readonly
    },
    year () {
      return new Date().getFullYear()
    },
    budget () {
      return Number(this.channel.budget || 0)
    },
    budgetPercent () {
      return ((this.channel.budget / this.campaign.budget) * 100) || 0
    },
    duration () {
      const start = moment(this.channel.start_date)
      const finish = moment(this.channel.finish_date)
      return finish.diff(start, 'days')
    },
    areas () {
      return this.channel.zones
    },
    zonesOptions () {
      return [allOption()].concat(this.zones)
    },
    dateRangeOk () {
      const minTime = new Date(this.campaign.start_date).getTime()
      const maxTime = new Date(this.campaign.finish_date).getTime()
      const startTime = moment(this.startDate).endOf('day').toDate().getTime()
      const endTime = moment(this.finishDate).startOf('day').toDate().getTime()
      return startTime >= minTime && endTime <= maxTime
    },
    roasGoalValid () {
      const n = Number(this.channel.roasGoal)
      return !isNaN(n) && n >= MIN_ROAS_GOAL && n <= MAX_ROAS_GOAL
    },
    headlines () {
      return this.channel.headlines || []
    },
    descriptions () {
      return this.channel.descriptions || []
    },
    keywords () {
      return this.channel.keywords || []
    },
    channelInsertionOrders () {
      return this.channel.insertion_orders || []
    },
    insertionOrders () {
      return this.channelInsertionOrders.map(io => ({
        name: io.name,
        pacingMaxDailySpend: io.pacing_max_daily_spend,
        pacingPeriod: this.pacingPeriodOptions.find(o => o.value === io.pacing_period),
        pacingType: this.pacingTypeOptions.find(o => o.value === io.pacing_type)
      }))
    },
    canAddInsertionOrder () {
      const insertionLimit = this.insertionOrders.length < INSERTION_ORDER_LIMIT
      const formValid = this.newInsertionOrder.name && this.newInsertionOrder.pacingPeriod && this.newInsertionOrder.pacingType
      return insertionLimit && formValid
    },
    channelLineItems () {
      return this.channel.line_items || []
    },
    lineItems () {
      return this.channelLineItems.map(li => ({
        name: li.name,
        markupPercentage: li.markup_percentage,
        creatives: this.creativeOptions.filter(co => li.creative_ids.indexOf(String(co.value)) !== -1),
        targetType: this.targetTypeOptions.find(o => o.value === li.target_name) || {
          label: li.target_name,
          value: li.target_name
        }
      }))
    },
    canAddLineItem () {
      const insertionLimit = this.lineItems.length < LINE_ITEM_LIMIT
      const formValid = this.newLineItem.name && this.newLineItem.targetType
      return insertionLimit && formValid
    },
    creativeOptionsWithIds () {
      return this.creativeOptionsFiltered
    }
  },
  created () {},
  async mounted () {
    this.loadChannel()
  },
  beforeDestroy () {},
  methods: {
    async loadChannel () {
      if (this.isLIA) {
        await this.fetchMerchants()
      }
      if (this.isDV360) {
        this.fetchCreatives()
        this.fetchTargetTypes()
      }

      // NOTE: To prevent POST to campaign endpoint and POST a new channel the first time we need to set the channel id
      if (this.$route.params.channelid && this.$route.params.channelid !== 'new') {
        this.$set(this.channel, 'id', this.$route.params.channelid)
      }

      this.$set(this.channel, 'isLocalInventory', this.isLocalInventory)
      this.channel.bid_strategy && this.$set(this.channel, 'strategy', this.strategyOptions.find(o => o.value === this.channel.bid_strategy))
      this.channel.merchant_center_id && this.$set(this.channel, 'merchant', this.merchantOptions.find(o => o.value === this.channel.merchant_center_id))
      // Fix is value is zero
      !isNaN(this.channel.priority) && this.$set(this.channel, 'priority', this.priorityOptions.find(o => Number(o.value) === Number(this.channel.priority)))
      this.channel.max_bid && this.$set(this.channel, 'maximumBid', this.channel.max_bid)
      this.channel.roas_goal && this.$set(this.channel, 'roasGoal', this.channel.roas_goal)
      this.$set(this.channel, 'isLocalInventory', this.channel.is_local_inventory)
      this.channel.network_settings && this.channel.network_settings.length && this.$set(this.channel, 'networks', this.channel.network_settings.map(n => this.networkOptions.find(o => o.value === n)))
      !!this.channel.final_url && this.$set(this.channel, 'finalURL', this.channel.final_url)
      !!this.channel.target_roas && this.$set(this.channel, 'roasGoal', this.channel.target_roas)
      !!this.channel.maximumBid && this.$set(this.channel, 'maximumBid', this.channel.maximumBid)
      !!this.channel.target_cpa && this.$set(this.channel, 'cpaGoal', this.channel.target_cpa)

      if (this.isDV360) {
        if (this.channel.mode) {
          this.$set(this.channel, 'mode', this.channelDV360Options.find(o => o.value === this.channel.mode).value)
        } else {
          this.$set(this.channel, 'mode', CHANNEL_DV360_OPTIONS.CREATE_UPDATE.value)
        }

        // Disable add line item if channel is not new
        this.channelDV360Options.find(o => o.value === CHANNEL_DV360_OPTIONS.ADD_LINE_ITEM.value).disabled = !this.isNewChannel

        if (this.channel.insertion_orders && this.channel.insertion_orders.length) {
          // Set insertion order
          const insertionOrder = {
            name: this.channel.insertion_orders[0].name,
            pacingMaxDailySpend: this.channel.insertion_orders[0].pacing_max_daily_spend,
            pacingPeriod: this.pacingPeriodOptions.find(o => o.value === this.channel.insertion_orders[0].pacing_period),
            pacingType: this.pacingTypeOptions.find(o => o.value === this.channel.insertion_orders[0].pacing_type)
          }
          this.newInsertionOrder = insertionOrder

          // Set line items
          const lineItems = {
            name: this.channel.line_items[0].name,
            markupPercentage: this.channel.line_items[0].markup_percentage,
            // Wait to fetch creatives -> fetchCreatives()
            creatives: [],
            targetType: this.targetTypeOptions.find(o => o.value === this.channel.line_items[0].target_name) || {
              label: this.channel.line_items[0].target_name,
              value: this.channel.line_items[0].target_name
            }
          }
          this.newLineItem = lineItems
        }
      }
      setTimeout(() => { this.initialChannel = { ...this.channel } }, 1000)
    },
    async fetchMerchants () {
      try {
        const shop = this.campaign.shops[0]
        const shopId = shop && shop.id
        const data = await googleAdsService.getMerchants(shopId)
        this.merchantOptions = data
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[tecnomat-google-channel-sidebar.js] Error fetching merchants', err)
      }
    },
    async fetchCreatives () {
      try {
        const data = await dv360Service.getCreatives()
        this.creativeOptions = data
        this.creativeOptionsFiltered = data.map(o => ({ ...o, id: o.value, name: o.label }))
        this.newLineItem.creatives = this.channel.line_items && this.channel.line_items.length
          ? this.creativeOptions.filter(co => this.channel.line_items[0].creative_ids.indexOf(String(co.value)) !== -1).map(co => ({
            ...co,
            id: parseInt(this.channel.line_items[0].creative_ids.find(id => id === String(co.value)))
          }))
          : []
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[tecnomat-google-channel-sidebar.js] Error fetching creatives', err)
      }
    },
    async fetchTargetTypes () {
      try {
        const data = await dv360Service.getTargetTypes()
        this.targetTypeOptions = data
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[tecnomat-google-channel-sidebar.js] Error fetching target types', err)
      }
    },
    async fetchCampaigns () {
      try {
        this.errorFetchingCampaignOptions = ''
        this.loadingCampignOptions = true

        const data = await dv360Service.getCampaigns()

        this.campaignOptions = data
        this.campaignItemsOptions = data
        this.loadingCampignOptions = false
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[tecnomat-google-channel-sidebar.js] Error fetching campaigns', err)
        this.errorFetchingCampaignOptions = 'Error fetching campaigns'
        this.loadingCampignOptions = false
      }
    },
    async fetchInsertionOrders (campaignId) {
      try {
        this.errorFetchingInsertionOrderOptions = ''
        this.loadingInsertionOrderOptions = true

        const data = await dv360Service.getInsertionOrders(campaignId)

        this.insertionOrderOptions = data
        this.insertionOrderItemsOptions = data
        this.loadingInsertionOrderOptions = false
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[tecnomat-google-channel-sidebar.js] Error fetching insertion orders', err)
        this.errorFetchingInsertionOrderOptions = 'Error fetching insertion orders'
        this.loadingInsertionOrderOptions = false
      }
    },
    updateScrollbar () {
      window.setTimeout(() => {
        if (this.$refs.scrollbar) this.$refs.scrollbar.update()
      }, 250)
    },
    onDatepickerChange (dates) {
      const [start, finish] = dates
      this.channel.start_date = start
      this.channel.finish_date = finish
    },
    selectStrategy (value) {
      this.$set(this.channel, 'strategy', value)
      if (this.$refs.strategySelector) this.$refs.strategySelector.close()
    },
    selectMerchant (value) {
      this.$set(this.channel, 'merchant', value)
      if (this.$refs.merchantSelector) this.$refs.merchantSelector.close()
    },
    selectPriority (value) {
      this.$set(this.channel, 'priority', value)
      if (this.$refs.prioritySelector) this.$refs.prioritySelector.close()
    },
    selectNetwork (value) {
      this.$set(this.channel, 'networks', value)
    },
    selectCampaignGoal (value) {
      this.$set(this.channel, 'campaign_goal', value)
      if (this.$refs.campaignGoalSelector) this.$refs.campaignGoalSelector.close()
    },
    selectPerformanceGoal (value) {
      this.$set(this.channel, 'performance_goal', value)
      if (this.$refs.performanceGoalSelector) this.$refs.performanceGoalSelector.close()
    },
    selectFrecuencyCapPeriod (value) {
      this.$set(this.channel, 'frecuency_cap_period', value)
      if (this.$refs.frecuencyCapPeriodSelector) this.$refs.frecuencyCapPeriodSelector.close()
    },
    selectNewPacingPeriod (value) {
      this.newInsertionOrder.pacingPeriod = value

      const pacingPeriod = value.value
      const newInsertionOrder = this.channel.insertion_orders ? { ...this.channel.insertion_orders[0], pacing_period: pacingPeriod } : { pacing_period: pacingPeriod }
      this.$set(this.channel, 'insertion_orders', [newInsertionOrder])

      if (this.$refs.newPacingPeriodSelector) this.$refs.newPacingPeriodSelector.close()
    },
    selectNewPacingType (value) {
      this.newInsertionOrder.pacingType = value

      const pacingType = value.value
      const newInsertionOrder = this.channel.insertion_orders ? { ...this.channel.insertion_orders[0], pacing_type: pacingType } : { pacing_type: pacingType }
      this.$set(this.channel, 'insertion_orders', [newInsertionOrder])

      if (this.$refs.newPacingTypeSelector) this.$refs.newPacingTypeSelector.close()
    },
    selectNewCreatives (value) {
      this.newLineItem.creatives = value

      const ids = value.map(c => String(c.value))
      const newLineItem = this.channel.line_items ? { ...this.channel.line_items[0], creative_ids: ids } : { creative_ids: ids }

      this.$set(this.channel, 'line_items', [newLineItem])
    },
    selectNewTargetType (value) {
      this.newLineItem.targetType = value

      const targetName = value.value
      const newLineItem = this.channel.line_items ? { ...this.channel.line_items[0], target_name: targetName } : { target_name: targetName }
      this.$set(this.channel, 'line_items', [newLineItem])

      if (this.$refs.newTargetTypeSelector) this.$refs.newTargetTypeSelector.close()
    },
    selectCampaign (data) {
      this.insertionOrderOptions = []
      this.$set(this.channel, 'line_item', null)
      this.$set(this.channel, 'campaign', data)

      if (this.$refs.campaignSelector) this.$refs.campaignSelector.close()

      // Fill form with campaign data
      this.$set(this.channel, 'budget', data.budget || 1)
      this.$set(this.channel, 'frecuency_cap_amount', data.frecuency_cap_amount)
      // NOTE: Disable selected campaign dates by costumer request sc-383099
      // this.$set(this.channel, 'finish_date', data.finish_date)
      // this.$set(this.channel, 'start_date', data.start_date)
      data.campaign_goal && this.$set(this.channel, 'campaign_goal', this.campaignGoalOptions.find(o => o.value === data.campaign_goal))
      data.performance_goal && this.$set(this.channel, 'performance_goal', this.performanceGoalOptions.find(o => o.value === data.performance_goal))
      data.frecuency_cap_period && this.$set(this.channel, 'frecuency_cap_period', this.frecuencyCapPeriodOptions.find(o => o.value === data.frecuency_cap_period))
    },
    selectLineOrderItem (data) {
      this.$set(this.channel, 'insertion_orders', [])
      this.$set(this.channel, 'line_item', data)
      if (this.$refs.lineItemSelector) this.$refs.lineItemSelector.close()

      const newInsertionOrder = {
        name: data.name,
        pacing_period: data.pacing_period,
        pacing_type: data.pacing_type,
        pacing_max_daily_spend: data.pacing_max_daily_spend
      }

      const insertionOrder = {
        name: newInsertionOrder.name,
        pacingMaxDailySpend: newInsertionOrder.pacing_max_daily_spend,
        pacingPeriod: this.pacingPeriodOptions.find(o => o.value === newInsertionOrder.pacing_period),
        pacingType: this.pacingTypeOptions.find(o => o.value === newInsertionOrder.pacing_type)
      }
      this.newInsertionOrder = insertionOrder

      this.$set(this.channel, 'insertion_order_id', data.insertion_order_id)
      this.$set(this.channel, 'insertion_orders', this.channelInsertionOrders.concat(newInsertionOrder))
    },
    setKeywords (value) {
      this.$set(this.channel, 'keywords', value)
    },
    addHeadline () {
      if (!this.newHeadline) {
        return
      }
      const headlines = (this.channel.headlines || []).concat(this.newHeadline)
      this.$set(this.channel, 'headlines', headlines)
      this.newHeadline = ''
    },
    removeHeadline (value) {
      const headlines = (this.channel.headlines || []).filter(h => h !== value)
      this.$set(this.channel, 'headlines', headlines)
    },
    addDescription () {
      if (!this.newDescription) {
        return
      }
      const descriptions = (this.channel.descriptions || []).concat(this.newDescription)
      this.$set(this.channel, 'descriptions', descriptions)
      this.newDescription = ''
    },
    removeDescription (value) {
      const descriptions = (this.channel.descriptions || []).filter(h => h !== value)
      this.$set(this.channel, 'descriptions', descriptions)
    },
    validateUrl (value, field) {
      if (value && value.length < 3) return
      // eslint-disable-next-line no-useless-escape
      const regex = /^(?:\w+:)?\/\/([^\s\.]+\.\S{2}|localhost[\:?\d]*)\S*$/
      if (regex.test(value)) {
        this.errors[field] = ''
      } else {
        this.errors[field] = i18n.t('INVALIDA_URL_FORMAT')
      }
    },
    onCheckboxChange () {
      this.isLocalInventory = !this.isLocalInventory
    },
    resetNewInsertionOrder () {
      this.newInsertionOrder = {
        name: '',
        pacingPeriod: null,
        pacingType: null,
        pacingMaxDailySpend: 0
      }
    },
    addNewInsertionOrder () {
      if (!this.canAddInsertionOrder) {
        return
      }

      const newInsertionOrder = {
        name: this.newInsertionOrder.name,
        pacing_period: this.newInsertionOrder.pacingPeriod.value,
        pacing_type: this.newInsertionOrder.pacingType.value,
        pacing_max_daily_spend: this.newInsertionOrder.pacingMaxDailySpend
      }

      this.$set(this.channel, 'insertion_orders', this.channelInsertionOrders.concat(newInsertionOrder))
      // this.resetNewInsertionOrder()
    },
    removeInsertionOrder (index) {
      this.$set(this.channel, 'insertion_orders', this.channelInsertionOrders.filter((_, idx) => idx !== index))
    },
    resetNewLineItem () {
      this.newLineItem = {
        name: '',
        markupPercentage: 0,
        creatives: [],
        targetType: null
      }
    },
    addNewLineItem () {
      if (!this.canAddLineItem) {
        return
      }

      const newLineItem = {
        name: this.newLineItem.name,
        markup_percentage: this.newLineItem.markupPercentage,
        creative_ids: this.newLineItem.creatives.map(c => String(c.value)),
        target_name: this.newLineItem.targetType.value
      }

      this.$set(this.channel, 'line_items', this.channelLineItems.concat(newLineItem))
      // this.resetNewLineItem()
    },
    removeLineItem (index) {
      this.$set(this.channel, 'line_items', this.channelLineItems.filter((_, idx) => idx !== index))
    },
    clearForm () {
      this.errors = {}
      this.$set(this.channel, 'budget', 0)
      this.$set(this.channel, 'frecuency_cap_amount', 0)
      // NOTE: Disable selected campaign dates by costumer request sc-383099
      // this.$set(this.channel, 'finish_date', null)
      // this.$set(this.channel, 'start_date', null)
      this.$set(this.channel, 'campaign_goal', null)
      this.$set(this.channel, 'performance_goal', null)
      this.$set(this.channel, 'frecuency_cap_period', null)
      this.$set(this.channel, 'insertion_orders', [])
      this.$set(this.channel, 'line_items', [])
      this.$set(this.channel, 'insertion_order_id', null)
      this.$set(this.channel, 'zones', [])
      this.$set(this.channel, 'keywords', [])
      this.newInsertionOrder = {
        name: '',
        pacingPeriod: null,
        pacingType: null,
        pacingMaxDailySpend: 0
      }
      this.newLineItem = {
        name: '',
        markupPercentage: 0,
        creatives: [],
        targetType: null
      }
    },
    searchCreatives: function (query) {
      const findedCreativities = this.creativeOptions.filter(
        creativity => creativity.label.toLowerCase().indexOf(query.toLowerCase()) !== -1
      ).map(o => ({ ...o, id: o.value, name: o.label }))

      this.creativeOptionsFiltered = findedCreativities
    },
    campaignSearch: function (query) {
      const findedCampaigns = this.campaignOptions.filter(
        campaign => campaign.label.toLowerCase().indexOf(query.toLowerCase()) !== -1
      )

      this.campaignItemsOptions = findedCampaigns
    },
    insertionOrderSearch: function (query) {
      const findedInsertionOrders = this.insertionOrderOptions.filter(
        insertionOrder => insertionOrder.label.toLowerCase().indexOf(query.toLowerCase()) !== -1
      )

      this.insertionOrderItemsOptions = findedInsertionOrders
    }
  },
  filters: {
    date (value) {
      const date = value instanceof Date ? value : new Date(value)
      return date.toLocaleDateString()
    }
  },
  watch: {
    // Form validation
    'channel.finalURL': {
      handler (value) {
        this.validateUrl(value, 'channel.finalURL')
      }
    },
    'channel.campaign': {
      handler (data) {
        if (data) {
          this.fetchInsertionOrders(data.value)
        }
      }
    },
    'channel.mode': {
      handler (data) {
        this.$set(this.channel, 'campaign', null)
        this.$set(this.channel, 'line_item', null)
        if (data === CHANNEL_DV360_OPTIONS.ADD_LINE_ITEM.value) {
          this.clearForm()
          if (this.campaignOptions.length === 0) {
            this.fetchCampaigns()
          }
        } else if (this.isNewChannel) {
          this.clearForm()
        }
      }
    },
    'newInsertionOrder.name': {
      handler (value) {
        const newInsertionOrder = this.channel.insertion_orders ? { ...this.channel.insertion_orders[0], name: value } : { name: value }
        this.$set(this.channel, 'insertion_orders', [newInsertionOrder])
      }
    },
    'newInsertionOrder.pacingMaxDailySpend': {
      handler (value) {
        const newInsertionOrder = this.channel.insertion_orders ? { ...this.channel.insertion_orders[0], pacing_max_daily_spend: value } : { pacing_max_daily_spend: value }
        this.$set(this.channel, 'insertion_orders', [newInsertionOrder])
      }
    },
    'newLineItem.name': {
      handler (value) {
        const newLineItem = this.channel.line_items ? { ...this.channel.line_items[0], name: value } : { name: value }
        this.$set(this.channel, 'line_items', [newLineItem])
      }
    },
    'newLineItem.markupPercentage': {
      handler (value) {
        const newLineItem = this.channel.line_items ? { ...this.channel.line_items[0], markup_percentage: value } : { markup_percentage: value }
        this.$set(this.channel, 'line_items', [newLineItem])
      }
    },
    channel: {
      deep: true,
      handler (value) {
        // Compare the initial channel with the current one
        // to check if the channel has been modified
        if (this.initialChannel) {
          const isModified = !isEqual(this.initialChannel, value)
          this.$emit('channelChange', isModified)
        }
      }
    }
  }
}
